'use strict'

/**
 * Mock API seam for the settlement-docs endpoint family.
 *
 * Answers: "how do I talk to the settlement-docs endpoint family?"
 *
 * THE SINGLE SEAM consumed by Vuex and the dialog/card components for
 * everything settlement-document-related (UCG-2210). Mirrors the v1
 * endpoint shape so the eventual backend swap is a body-only rewrite of
 * these four functions.
 *
 * Endpoint mirror:
 *   - presignSettlementDoc       -> POST   /refinery/master-lots/:id/settlement/documents/presign
 *   - confirmSettlementDoc       -> PATCH  /refinery/master-lots/:id/settlement/documents/:docId/confirm
 *   - deleteSettlementDoc        -> DELETE /refinery/master-lots/:id/settlement/documents/:docId
 *   - fetchSettlementDocBlobUrl  -> v1: returns a `signedGetUrl` from the
 *                                   inline-served document; demo: reads the
 *                                   blob from IndexedDB and creates an
 *                                   object URL on the fly.
 *
 * Vuex action signatures, dialog/card props, and the lazy-URL accessor
 * pattern in `MasterLotSettlementCard.vue` all stay identical across the
 * demo → v1 swap.
 *
 * V1 RIP-OUT: rewrite each body to `request.{post,patch,delete,get}`
 * against the real endpoints and delete the blob-store sidetrip in
 * presign/delete/fetch. Signatures + Vuex action contracts stay
 * identical so the dialog/card components never see the swap.
 */

import {prefixedId} from '@/util/prefixedId'
import {
  deleteSettlementDocBlob,
  getSettlementDocBlob,
  putSettlementDocBlob,
} from './blobStore'
import {
  MASTER_LOT_SETTLEMENT_DOC_ALLOWED_MIME_TYPES,
  MASTER_LOT_SETTLEMENT_DOC_MAX_UPLOAD_BYTES,
  MASTER_LOT_SETTLEMENT_DOC_MAX_UPLOAD_MB,
} from './uploadConstraints'

const newDocumentId = () => prefixedId('doc')

/**
 * DEMO BODY: validate inputs, park the bytes in IndexedDB, hand back a
 * stable `documentId` + sentinel `putUrl` for the orchestrator to "PUT" to.
 *
 * V1 BODY: POST to the presign endpoint and forward the server's
 * `{documentId, putUrl}` straight back. The blob-store sidetrip goes away.
 */
export async function presignSettlementDoc(
  {masterLotId, filename, contentType, contentLength},
  {file}
) {
  if (!masterLotId) throw new Error('masterLotId required')
  if (!filename) throw new Error('filename required')
  if (!MASTER_LOT_SETTLEMENT_DOC_ALLOWED_MIME_TYPES.includes(contentType)) {
    throw new Error(`unsupported MIME type: ${contentType}`)
  }
  if (!Number.isFinite(contentLength) || contentLength <= 0) {
    throw new Error('contentLength must be positive')
  }
  if (contentLength > MASTER_LOT_SETTLEMENT_DOC_MAX_UPLOAD_BYTES) {
    throw new Error(`file too large; max ${MASTER_LOT_SETTLEMENT_DOC_MAX_UPLOAD_MB} MB`)
  }
  const documentId = newDocumentId()
  await putSettlementDocBlob(documentId, file)
  return {
    documentId,
    putUrl: `demo://settlement-docs/${masterLotId}/${documentId}`,
  }
}

/**
 * DEMO BODY: synthesise the document record the dialog wants (matches
 * what v1 will return in the confirm response). Pure — no blob-store call.
 *
 * V1 BODY: PATCH the confirm endpoint with the caption + size; server
 * runs HeadObject against S3 to verify the upload landed, then returns
 * the persisted document row.
 */
export async function confirmSettlementDoc({
  documentId,
  filename,
  mimeType,
  byteSize,
  caption,
}) {
  if (!documentId) throw new Error('documentId required')
  return {
    document: {
      id: documentId,
      filename,
      mimeType,
      byteSize,
      caption: caption || '',
      uploadedAt: new Date().toISOString(),
    },
  }
}

/**
 * DEMO BODY: drop the blob from IndexedDB. Demo doesn't actually need
 * `masterLotId` (the documentId is a UUID), but we require it on the
 * signature so the v1 swap is a body-only rewrite — v1 hits
 * `DELETE /refinery/master-lots/:id/settlement/documents/:docId` and
 * needs both segments at the call site.
 *
 * V1 BODY: DELETE the doc endpoint; server soft-deletes the row and either
 * leaves the S3 bytes in place (per PR #1183 AD-6) or schedules cleanup.
 */
export async function deleteSettlementDoc({masterLotId, documentId}) {
  if (!masterLotId) throw new Error('masterLotId required')
  if (!documentId) throw new Error('documentId required')
  await deleteSettlementDocBlob(documentId)
  return {masterLotId, documentId}
}

/**
 * DEMO BODY: read the blob from IndexedDB, mint a fresh `blob:` URL via
 * `URL.createObjectURL`. Returns null when the store has no bytes for the
 * id (D003's pre-populated stub docs hit this path; the read-side card
 * should render gracefully — "Open" disabled with "(no file)" hint).
 *
 * V1 BODY: either returns `signedGetUrl` from the inline-served document
 * (PR #1183's pattern) or fetches a fresh signed URL from a dedicated
 * endpoint. The card's `{[docId]: url}` cache + `revokeObjectURL` on
 * destroy still applies — for v1 the URLs are short-lived signed S3 URLs.
 */
export async function fetchSettlementDocBlobUrl(documentId) {
  if (!documentId) return null
  const blob = await getSettlementDocBlob(documentId)
  if (!blob) return null
  return URL.createObjectURL(blob)
}
